const RECHARGE_WIDGET_SELECTOR = 'recharge-subscription-widget';
const STICKY_VISIBLE_CLASS = 'trifecta-sticky-purchase-visible';
const RECHARGE_POSITION_READY = 'ready';
const RECHARGE_POSITION_PENDING = 'pending';
const RECHARGE_POSITION_UNAVAILABLE = 'unavailable';
const RECHARGE_MEASURE_INTERVAL = 100;
const RECHARGE_MEASURE_ATTEMPTS = 100;

class TrifectaProductQuickAdd extends HTMLElement {
  connectedCallback() {
    this.productForm = document.getElementById(this.getAttribute('form'));
    this.footer = document.querySelector('.footer');
    this.panel = this.querySelector('[data-sticky-purchase-panel]');
    this.cta = this.querySelector('[data-sticky-purchase-cta]');
    this.closeButton = this.querySelector('[data-sticky-purchase-close]');
    this.priceElement = this.querySelector('[data-sticky-purchase-price]');
    this.labelElement = this.querySelector('[data-sticky-purchase-label]');
    this.rechargeSlot = this.querySelector('[data-sticky-recharge-slot]');
    this.loadingElement = this.querySelector('[data-sticky-purchase-loading]');
    this.errorElement = this.querySelector('[data-sticky-purchase-error]');
    this.productInfo = this.productForm?.closest('.product-info');
    this.variantPrices = this.parseVariantPrices();
    this.expanded = false;
    this.pendingSubmission = false;

    if (!this.productForm || !this.panel || !this.cta) {
      return;
    }

    this.onCtaClick = this.handleCtaClick.bind(this);
    this.onCloseClick = this.handleCloseClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onProductSubmit = this.handleProductSubmit.bind(this);
    this.onVariantChange = this.handleVariantChange.bind(this);
    this.onCartError = this.handleCartError.bind(this);
    this.onVariantAdded = this.handleVariantAdded.bind(this);
    this.onRechargeChange = this.handleRechargeChange.bind(this);
    this.onViewportChange = this.scheduleVisibilityUpdate.bind(this);
    this.onPanelScroll = this.positionRechargeWidgetInPanel.bind(this);

    this.cta.addEventListener('click', this.onCtaClick);
    this.closeButton.addEventListener('click', this.onCloseClick);
    this.addEventListener('keydown', this.onKeydown);
    this.productForm.addEventListener('submit', this.onProductSubmit);
    this.productForm.addEventListener('variant:change', this.onVariantChange);
    this.productForm.addEventListener('cart:error', this.onCartError);
    this.productForm.addEventListener('variant:add', this.onVariantAdded);
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange);
    this.panel.addEventListener('scroll', this.onPanelScroll, { passive: true });

    if (this.productInfo) {
      this.productInfoObserver = new MutationObserver(() => this.captureRechargeWidget());
      this.productInfoObserver.observe(this.productInfo, { childList: true, subtree: true });
    }

    this.captureRechargeWidget();
    this.scheduleVisibilityUpdate();

    if ('customElements' in window) {
      customElements.whenDefined(RECHARGE_WIDGET_SELECTOR).then(() => {
        if (this.isConnected) {
          this.captureRechargeWidget();
        }
      });
    }
  }

  disconnectedCallback() {
    this.productInfoObserver?.disconnect();
    this.rechargeObserver?.disconnect();
    window.clearInterval(this.rechargeReadyInterval);
    window.cancelAnimationFrame(this.visibilityFrame);
    this.stopWaitingForRechargeWidget();

    this.cta?.removeEventListener('click', this.onCtaClick);
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.productForm?.removeEventListener('submit', this.onProductSubmit);
    this.productForm?.removeEventListener('variant:change', this.onVariantChange);
    this.productForm?.removeEventListener('cart:error', this.onCartError);
    this.productForm?.removeEventListener('variant:add', this.onVariantAdded);
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    this.panel?.removeEventListener('scroll', this.onPanelScroll);

    this.detachRechargeRoot();
    this.restoreRechargeWidget();
    this.rechargePlaceholder?.remove();
    document.body.classList.remove(STICKY_VISIBLE_CLASS);
  }

  parseVariantPrices() {
    try {
      return JSON.parse(this.querySelector('[data-sticky-variant-prices]')?.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  scheduleVisibilityUpdate() {
    if (this.visibilityFrame) {
      return;
    }

    this.visibilityFrame = window.requestAnimationFrame(() => {
      this.visibilityFrame = null;
      this.updateVisibility();
    });
  }

  updateVisibility() {
    const formBounds = this.productForm.getBoundingClientRect();
    const footerBounds = this.footer?.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const formPassed = formBounds.bottom <= 0;
    const footerReached = footerBounds ? footerBounds.top <= viewportHeight : false;
    const isVisible = formPassed && !footerReached;
    this.classList.toggle('is-visible', isVisible);
    document.body.classList.toggle(STICKY_VISIBLE_CLASS, isVisible);

    if (!isVisible) {
      this.closePanel({ restoreFocus: false });
    } else if (this.expanded) {
      this.positionRechargeWidgetInPanel();
    }
  }

  handleCtaClick(event) {
    if (this.expanded) {
      return;
    }

    this.captureRechargeWidget();

    if (!this.hasExpandableOptions()) {
      return;
    }

    event.preventDefault();

    if (!this.canPromoteRechargeWidget()) {
      this.revealCanonicalPurchaseOptions();
      return;
    }

    this.openPanel();
  }

  handleCloseClick() {
    this.closePanel({ restoreFocus: true });
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.expanded) {
      event.preventDefault();
      this.closePanel({ restoreFocus: true });
    }
  }

  handleProductSubmit() {
    if (!this.expanded || !this.productForm.checkValidity()) {
      return;
    }

    this.pendingSubmission = true;

    if (this.errorElement) {
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }

    this.closePanel({ restoreFocus: false });
  }

  openPanel() {
    this.captureRechargeWidget();
    this.expanded = true;
    this.panel.hidden = false;
    this.cta.setAttribute('aria-expanded', 'true');
    this.cta.setAttribute('aria-label', this.dataset.addLabel);
    this.classList.add('is-expanded');

    const position = this.positionRechargeWidgetInPanel();

    if (position === RECHARGE_POSITION_UNAVAILABLE) {
      return;
    }

    this.closeButton.focus();
  }

  abandonPanel() {
    this.closePanel({ restoreFocus: this.contains(document.activeElement) });
    this.revealCanonicalPurchaseOptions();
  }

  // Recharge renders asynchronously, so an unmeasurable widget keeps the panel
  // in its loading state until it can be docked or the wait is exhausted.
  waitForRechargeWidget() {
    if (this.rechargeMeasureInterval) {
      return;
    }

    let attempts = 0;
    this.rechargeMeasureInterval = window.setInterval(() => {
      attempts += 1;

      if (!this.expanded) {
        this.stopWaitingForRechargeWidget();
        return;
      }

      const position = this.positionRechargeWidgetInPanel();

      if (position !== RECHARGE_POSITION_PENDING) {
        return;
      }

      if (attempts < RECHARGE_MEASURE_ATTEMPTS) {
        return;
      }

      this.stopWaitingForRechargeWidget();
      this.abandonPanel();
    }, RECHARGE_MEASURE_INTERVAL);
  }

  stopWaitingForRechargeWidget() {
    window.clearInterval(this.rechargeMeasureInterval);
    this.rechargeMeasureInterval = null;
  }

  closePanel({ restoreFocus }) {
    if (!this.expanded && this.panel.hidden) {
      return;
    }

    this.expanded = false;
    this.panel.hidden = true;
    this.cta.setAttribute('aria-expanded', 'false');
    this.cta.setAttribute(
      'aria-label',
      this.hasExpandableOptions() ? this.dataset.openLabel : this.dataset.addLabel
    );
    this.classList.remove('is-expanded');
    this.stopWaitingForRechargeWidget();
    this.restoreRechargeWidget();

    if (restoreFocus && this.classList.contains('is-visible')) {
      this.cta.focus();
    }
  }

  handleVariantChange(event) {
    const variant = event.detail.variant;
    const isAvailable = Boolean(variant?.available);

    this.cta.disabled = !isAvailable;
    this.labelElement.textContent = !variant
      ? window.themeVariables.strings.unavailableButton
      : isAvailable
        ? window.themeVariables.strings.addToCartButton
        : window.themeVariables.strings.soldOutButton;

    if (variant && this.variantPrices[String(variant.id)]) {
      this.priceElement.textContent = this.variantPrices[String(variant.id)];
    }

    window.requestAnimationFrame(() => {
      this.captureRechargeWidget();
      this.syncRechargePrice();
    });
  }

  handleCartError(event) {
    const shouldReopenPanel = this.pendingSubmission && this.classList.contains('is-visible');
    this.pendingSubmission = false;

    if (this.errorElement) {
      this.errorElement.textContent = event.detail.error;
      this.errorElement.hidden = false;
    }

    if (shouldReopenPanel) {
      this.openPanel();
    }
  }

  handleVariantAdded() {
    this.pendingSubmission = false;

    if (this.errorElement) {
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }

    this.closePanel({ restoreFocus: false });
  }

  captureRechargeWidget() {
    const widget = this.findRechargeWidget();

    if (!widget) {
      return;
    }

    if (widget !== this.rechargeWidget) {
      this.deactivateRechargeLayer();
      this.detachRechargeRoot();
      this.rechargeWidget = widget;

      if (!this.rechargePlaceholder?.isConnected && widget.parentNode) {
        this.rechargePlaceholder = document.createElement('span');
        this.rechargePlaceholder.hidden = true;
        this.rechargePlaceholder.dataset.stickyRechargePlaceholder = '';
        widget.before(this.rechargePlaceholder);
      }
    }

    if (!this.expanded) {
      this.cta.setAttribute('aria-label', this.dataset.openLabel);
    }

    if (this.expanded) {
      this.positionRechargeWidgetInPanel();
    }

    this.attachRechargeRoot();
  }

  findRechargeWidget() {
    const widgets = Array.from(document.querySelectorAll(RECHARGE_WIDGET_SELECTOR));
    const matchingWidget = widgets.find(
      (widget) => widget.getAttribute('product-id') === this.dataset.productId
    );

    return matchingWidget || this.productInfo?.querySelector(RECHARGE_WIDGET_SELECTOR) || null;
  }

  attachRechargeRoot() {
    if (!this.rechargeWidget) {
      return;
    }

    if (!this.rechargeWidget.shadowRoot) {
      if (!this.rechargeReadyInterval) {
        let attempts = 0;
        this.rechargeReadyInterval = window.setInterval(() => {
          attempts += 1;

          if (this.rechargeWidget?.shadowRoot || attempts >= 100) {
            window.clearInterval(this.rechargeReadyInterval);
            this.rechargeReadyInterval = null;
            this.attachRechargeRoot();
          }
        }, 100);
      }

      return;
    }

    if (this.rechargeRoot === this.rechargeWidget.shadowRoot) {
      this.syncRechargePrice();
      return;
    }

    this.detachRechargeRoot();
    this.rechargeRoot = this.rechargeWidget.shadowRoot;
    this.rechargeRoot.addEventListener('change', this.onRechargeChange);
    this.rechargeRoot.addEventListener('input', this.onRechargeChange);
    this.rechargeObserver = new MutationObserver(() => {
      this.syncRechargePrice();

      if (this.expanded) {
        this.positionRechargeWidgetInPanel();
      }
    });
    this.rechargeObserver.observe(this.rechargeRoot, {
      attributes: true,
      childList: true,
      subtree: true
    });
    this.syncRechargePrice();
  }

  detachRechargeRoot() {
    this.rechargeObserver?.disconnect();
    this.rechargeObserver = null;

    if (this.rechargeRoot) {
      this.rechargeRoot.removeEventListener('change', this.onRechargeChange);
      this.rechargeRoot.removeEventListener('input', this.onRechargeChange);
    }

    this.rechargeRoot = null;
  }

  handleRechargeChange() {
    window.requestAnimationFrame(() => {
      this.syncRechargePrice();

      if (this.expanded) {
        this.positionRechargeWidgetInPanel();
      }
    });
  }

  syncRechargePrice() {
    if (!this.rechargeRoot || !this.priceElement) {
      return;
    }

    const checkedInput = this.rechargeRoot.querySelector('input[name="purchaseOption"]:checked');
    const selectedOption = checkedInput?.closest('[part~="rc-purchase-option"], .rc-purchase-option')
      || this.rechargeRoot.querySelector('[part~="rc-purchase-option__selected"], [rc-selected]');

    if (!selectedOption) {
      return;
    }

    const isSubscription = checkedInput
      ? this.isSubscriptionInput(checkedInput)
      : Boolean(selectedOption.querySelector('[part~="rc-purchase-option__discounted-price"]'));
    const priceElement = isSubscription
      ? selectedOption.querySelector('[part~="rc-purchase-option__discounted-price"]')
      : selectedOption.querySelector(
        '[part~="rc-purchase-option__unit-price"], .rc-price:not(.strike-through)'
      );
    const fallbackPrice = selectedOption.querySelector(
      '[part~="rc-purchase-option__discounted-price"], [part~="rc-purchase-option__unit-price"], .rc-price:not(.strike-through)'
    );
    const priceText = this.getVisiblePriceText(priceElement || fallbackPrice);

    if (priceText) {
      this.priceElement.textContent = priceText;
    }
  }

  getVisiblePriceText(priceElement) {
    if (!priceElement) {
      return '';
    }

    const visiblePrice = priceElement.cloneNode(true);
    visiblePrice
      .querySelectorAll('.visually-hidden, [hidden], [aria-hidden="true"]')
      .forEach((element) => element.remove());

    return visiblePrice.textContent.replace(/\s+/g, ' ').trim();
  }

  positionRechargeWidgetInPanel() {
    // A widget that left the document can never be measured or promoted, so it
    // is a fallback case rather than something worth waiting on.
    if (
      !this.rechargeWidget?.isConnected
      || !this.rechargeSlot
      || !this.rechargePlaceholder?.isConnected
    ) {
      return this.handleRechargePosition(RECHARGE_POSITION_UNAVAILABLE);
    }

    if (!this.rechargeWidget.hasAttribute('data-trifecta-sticky-active')) {
      const canonicalBounds = this.rechargeWidget.getBoundingClientRect();
      const canonicalHeight = Math.max(
        canonicalBounds.height,
        this.rechargeWidget.scrollHeight
      );

      if (!canonicalHeight) {
        if (this.loadingElement) {
          this.loadingElement.hidden = false;
        }

        return this.handleRechargePosition(RECHARGE_POSITION_PENDING);
      }

      this.rechargeCanonicalHeight = canonicalHeight;
      this.rechargePlaceholder.hidden = false;
      this.rechargePlaceholder.style.display = 'block';
      this.rechargePlaceholder.style.blockSize = `${canonicalHeight}px`;
    }

    const initialSlotBounds = this.rechargeSlot.getBoundingClientRect();
    this.rechargeWidget.dataset.trifectaStickyActive = '';
    this.rechargeWidget.style.setProperty(
      '--trifecta-sticky-widget-left',
      `${initialSlotBounds.left}px`
    );
    this.rechargeWidget.style.setProperty(
      '--trifecta-sticky-widget-top',
      `${initialSlotBounds.top}px`
    );
    this.rechargeWidget.style.setProperty(
      '--trifecta-sticky-widget-width',
      `${initialSlotBounds.width}px`
    );
    this.rechargeWidget.style.removeProperty('--trifecta-sticky-widget-max-height');

    if (!this.activateRechargeLayer()) {
      return this.handleRechargePosition(RECHARGE_POSITION_UNAVAILABLE);
    }

    // Recharge content can become taller when it wraps at the narrower tray
    // width, so measure again only after the sticky width has been applied.
    const stickyWidgetBounds = this.rechargeWidget.getBoundingClientRect();
    const stickyWidgetHeight = Math.max(
      stickyWidgetBounds.height,
      this.rechargeWidget.scrollHeight
    );

    if (!stickyWidgetHeight) {
      if (this.loadingElement) {
        this.loadingElement.hidden = false;
      }

      return this.handleRechargePosition(RECHARGE_POSITION_PENDING);
    }

    this.rechargeSlot.style.minBlockSize = `${stickyWidgetHeight}px`;

    if (this.loadingElement) {
      this.loadingElement.hidden = true;
    }

    const slotBounds = this.rechargeSlot.getBoundingClientRect();
    const panelBounds = this.panel.getBoundingClientRect();
    const availableHeight = Math.max(120, panelBounds.bottom - slotBounds.top - 16);
    const visibleWidgetHeight = Math.min(stickyWidgetHeight, availableHeight);

    this.rechargeSlot.style.minBlockSize = `${visibleWidgetHeight}px`;

    const positionedSlotBounds = this.rechargeSlot.getBoundingClientRect();

    this.rechargeWidget.style.setProperty(
      '--trifecta-sticky-widget-left',
      `${positionedSlotBounds.left}px`
    );
    this.rechargeWidget.style.setProperty(
      '--trifecta-sticky-widget-top',
      `${positionedSlotBounds.top}px`
    );
    this.rechargeWidget.style.setProperty(
      '--trifecta-sticky-widget-width',
      `${positionedSlotBounds.width}px`
    );
    this.rechargeWidget.style.setProperty(
      '--trifecta-sticky-widget-max-height',
      `${visibleWidgetHeight}px`
    );

    return this.handleRechargePosition(RECHARGE_POSITION_READY);
  }

  handleRechargePosition(position) {
    if (!this.expanded) {
      return position;
    }

    if (position === RECHARGE_POSITION_PENDING) {
      this.waitForRechargeWidget();
      return position;
    }

    this.stopWaitingForRechargeWidget();

    if (position === RECHARGE_POSITION_UNAVAILABLE) {
      this.abandonPanel();
    }

    return position;
  }

  hasExpandableOptions() {
    return Boolean(this.rechargeWidget);
  }

  isSubscriptionInput(input) {
    const value = input.value.toLowerCase();
    return value.includes('subscription') || value.includes('subscribe') || value.includes('subsave');
  }

  canPromoteRechargeWidget() {
    return typeof this.rechargeWidget?.showPopover === 'function';
  }

  activateRechargeLayer() {
    if (!this.rechargeWidget) {
      return false;
    }

    if (this.rechargeUsesPopover && this.rechargeWidget.matches(':popover-open')) {
      return true;
    }

    if (!this.canPromoteRechargeWidget()) {
      return false;
    }

    this.rechargeWidget.setAttribute('popover', 'manual');

    try {
      if (!this.rechargeWidget.matches(':popover-open')) {
        this.rechargeWidget.showPopover();
      }

      this.rechargeUsesPopover = true;
      return true;
    } catch (error) {
      this.rechargeWidget.removeAttribute('popover');
      this.rechargeUsesPopover = false;
      return false;
    }
  }

  deactivateRechargeLayer() {
    if (this.rechargeUsesPopover && this.rechargeWidget?.isConnected) {
      try {
        if (this.rechargeWidget.matches(':popover-open')) {
          this.rechargeWidget.hidePopover();
        }
      } catch (error) {
        // The widget may already have left the top layer during a section re-render.
      }
    }

    this.rechargeWidget?.removeAttribute('popover');
    this.rechargeUsesPopover = false;
  }

  revealCanonicalPurchaseOptions() {
    this.restoreRechargeWidget();
    const target = this.rechargeWidget?.isConnected ? this.rechargeWidget : this.productForm;

    target.scrollIntoView({ block: 'center' });

    window.requestAnimationFrame(() => {
      const selectedOption = this.rechargeRoot?.querySelector(
        'input[name="purchaseOption"]:checked, input[name="purchaseOption"]'
      );

      selectedOption?.focus({ preventScroll: true });
    });
  }

  restoreRechargeWidget() {
    this.deactivateRechargeLayer();

    if (!this.rechargeWidget) {
      return;
    }

    delete this.rechargeWidget.dataset.trifectaStickyActive;
    this.rechargeWidget.style.removeProperty('--trifecta-sticky-widget-left');
    this.rechargeWidget.style.removeProperty('--trifecta-sticky-widget-top');
    this.rechargeWidget.style.removeProperty('--trifecta-sticky-widget-width');
    this.rechargeWidget.style.removeProperty('--trifecta-sticky-widget-max-height');
    this.rechargeSlot?.style.removeProperty('min-block-size');

    if (this.rechargePlaceholder?.isConnected) {
      this.rechargePlaceholder.hidden = true;
      this.rechargePlaceholder.style.removeProperty('display');
      this.rechargePlaceholder.style.removeProperty('block-size');
    }
  }
}

if (!window.customElements.get('trifecta-product-quick-add')) {
  window.customElements.define('trifecta-product-quick-add', TrifectaProductQuickAdd);
}
